import styled from 'styled-components';
import {
  FavouriteIcon as BaseFavouriteIcon,
  FavouriteFilledIcon as BaseFavouriteFilledIcon,
  mq,
  Caption,
  BodyText,
  ButtonSmallText
} from '@kvdbil/components';
import React, { MouseEvent, TouchEvent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '~/Locale';
import useProductCardIconsProps from '~/App/shared/components/ProductCard/hooks/useProductCardIconsProps';
import Auction from '~/App/shared/interfaces/Auction';
import { CardConditionStatus } from './CardConditionStatus';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import {
  FilterPageDocument,
  FilterPageQuery
} from '~/config/generated/graphql';
import { FilterPageData } from '~/App/shared/interfaces/store/CmsData';
import { getMainCategoryFromVehicleType } from '~/App/shared/helpers/general';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';
import { useFeatureFlagValue } from '~/App/shared/featureFlags';
import { useHasValueChanged } from '~/App/shared/hooks/useHasValueChanged';
import { favouriteCountPulse } from '~/App/shared/components/favouriteCountPulse';
import { websocketAuctionsById } from '~/App/shared/selectors/websockets';

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  position: relative;

  & > span {
    margin-right: 0.5rem;
  }
  & > :last-child {
    margin-right: 0;
  }

  svg {
    font-size: 2rem;
  }

  ${mq('tablet')} {
    svg {
      font-size: 1.5rem;
    }
  }
`;

const FavouriteCount = styled(ButtonSmallText)<{ $isUpdated: boolean }>`
  ${favouriteCountPulse}
`;

const FavouriteFilledIcon = styled(BaseFavouriteFilledIcon)`
  color: ${({ theme }) => theme.colors.tertiary.mainAlt};
`;

const FavouriteIcon = styled(BaseFavouriteIcon)`
  :hover {
    color: ${({ theme }) => theme.colors.tertiary.mainAlt};
  }
`;

const FavouriteIconWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PopupWindow = styled.div`
  top: 0.875rem;
  left: 0;
  position: absolute;
  border-radius: 0.5rem;
  border: solid 1px ${({ theme }) => theme.colors.grayscaleToned.light4};
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.colors.background.light};
  box-shadow: ${({ theme }) => theme.elevations.elevation4};
  z-index: 999;
`;

export interface Props {
  auction: Auction;
}

const Icons = ({ auction }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useIsDevice();
  const showFavouriteCount = useFeatureFlagValue(
    'show-auction-favourite-count'
  );
  const websocketAuctions = useSelector(websocketAuctionsById);
  const favouriteCount =
    websocketAuctions[auction.id]?.favouriteCount ?? auction.favouriteCount;
  const isFavouriteCountUpdated = useHasValueChanged(favouriteCount);
  const [showToolTip, setShowToolTip] = useState<boolean>(false);

  const mainCategory = getMainCategoryFromVehicleType(
    auction.processObject.vehicleType
  );
  const condition = auction.processObject.conditionClassification;

  const { isFavourite, toggleFavourite } = useProductCardIconsProps(auction);
  const { data: cmsData } = useCmsData<FilterPageQuery, FilterPageData>({
    storeKey: 'filterPage',
    storeNestedKey: mainCategory ?? 'all',
    query: FilterPageDocument,
    options: {
      variables: {
        mainCategory: mainCategory ?? 'all'
      }
    }
  });

  const toolTipData = useMemo(
    () => cmsData?.toolTips?.find(data => data.condition === condition),
    [cmsData?.toolTips, condition]
  );

  const handleToggleFavourite = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavourite();
  };

  const handleConditionStatusClick = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowToolTip(prevValue => !prevValue);
  };

  const handleMouseEnter = () => {
    setShowToolTip(true);
  };

  const handleMouseLeave = () => {
    setShowToolTip(false);
  };

  return (
    <Container>
      {condition && (
        <CardConditionStatus
          condition={condition}
          onEnter={handleMouseEnter}
          onLeave={handleMouseLeave}
          onClick={handleConditionStatusClick}
          onTouch={handleConditionStatusClick}
        />
      )}

      <FavouriteIconWrapper
        data-testid="favourite-icon-wrapper"
        onClick={handleToggleFavourite}
      >
        {showFavouriteCount && favouriteCount > 0 && (
          <FavouriteCount
            key={favouriteCount}
            $isUpdated={isFavouriteCountUpdated}
            data-testid="favourite-count"
          >
            {favouriteCount}
          </FavouriteCount>
        )}
        {isFavourite ? (
          <FavouriteFilledIcon
            data-testid="favourite-icon-solid"
            title={t('FILLED_FAVOURITE_ICON_TOOLTIP')}
          />
        ) : (
          <FavouriteIcon
            data-testid="favourite-icon"
            title={t('FAVOURITE_ICON_TOOLTIP')}
          />
        )}
      </FavouriteIconWrapper>
      {showToolTip && toolTipData?.infoText && (
        <PopupWindow
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleConditionStatusClick}
        >
          {isMobile ? (
            <BodyText>{toolTipData?.infoText}</BodyText>
          ) : (
            <Caption>{toolTipData?.infoText}</Caption>
          )}
        </PopupWindow>
      )}
    </Container>
  );
};

export default Icons;
